import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

import observationActions from 'actions/observationActions';
import lookupTableActions from 'actions/lookupTableActions';
import noteActions from 'actions/noteActions';
import evidenceActions from 'actions/evidenceActions';

import Grid from 'components/core/Grid';
import Sheet from 'components/core/Sheet';
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';

import Observation from 'domain/Observation';
import ObservationForm from 'components/ObservationForm';

const ObservationAdd = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { taxonomyNameId } = useParams();
    const options = useSelector(({ lookupTable }) => lookupTable.options);    
    const [observation, setObservation] = useState(new Observation());
    const [errors, setErrors] = useState({});

    const [initialNotes, setInitialNotes] = useState([]);

    useEffect(() => {
        if (taxonomyNameId && options.names.length > 0) {
            const name = options.names.find(name => name.id === taxonomyNameId);
            const newObservation = new Observation();
            newObservation.set('name', name);
            setObservation(newObservation);
        }
    }, [taxonomyNameId, options.names]);

    useEffect(() => {
        const filteredOptions = options.noteTypes.filter(option => {
            return option.get('filteredFor') === 'Observation' && option.get('label') !== 'Observation Restriction Note';
        })
        const startingNotes = []
        if (filteredOptions.length > 0) {
            filteredOptions.forEach((option) => {
                startingNotes.push({
                    type: option,
                    text: '',
                    isDefault: true
                });
            })
        }
        setInitialNotes(startingNotes);
    }, [options.noteTypes]);
    
    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
    }, [dispatch]);

    const handleSubmit = async (submittedObservation, submittedNotes, noteDiff, submittedEvidence, evidenceDiff) => {
        setErrors({});

        try {
            await submittedObservation.inspect();
            const newObservation = new Observation();
            newObservation.set('name', submittedObservation.get('name')?.id);
            newObservation.set('substantiation', submittedObservation.get('substantiation')?.id);
            newObservation.set('locDeterminization', submittedObservation.get('locDeterminization')?.id);
            newObservation.set('substrate', submittedObservation.get('substrate')?.id);
            newObservation.set('quality', submittedObservation.get('quality')?.id);
            newObservation.set('location', submittedObservation.get('location')?.id);
            newObservation.set('town', submittedObservation.get('town')?.id);
            newObservation.set('flag', submittedObservation.get('flag'));
            newObservation.set('observationRestriction', submittedObservation.get('observationRestriction'));
            
            const lat = submittedObservation.get('lat');
            const lng = submittedObservation.get('lng');
            newObservation.set('lat', lat ? parseFloat(lat) : null);
            newObservation.set('lng', lng ? parseFloat(lng) : null);
            newObservation.set('date', submittedObservation.get('date'));
            newObservation.set('isDetected', submittedObservation.get('isDetected'));
            newObservation.set('event', submittedObservation.get('event')?.id);
            newObservation.set('keyword', submittedObservation.get('keyword')?.id);
            newObservation.set('dateSpecificity', submittedObservation.get('dateSpecificity'));

            newObservation.set('observedBy', submittedObservation.get('observedBy')?.get('person')?.id);
            newObservation.set('reviewedBy', submittedObservation.get('reviewedBy')?.get('person')?.id);
            
            await newObservation.inspect();
            const saveObservationResult = await dispatch(observationActions.save(newObservation));
            const savedObservation = saveObservationResult.value;
            await Promise.all(submittedNotes.map(note => {
                if (note.type && note.text.trim()) {
                    return dispatch(noteActions.save({
                        observation: savedObservation.id,
                        type: note.type.id,
                        text: note.text,
                        parent: savedObservation.id
                    }));
                }
                return Promise.resolve();
            }))
            
            if (submittedEvidence && submittedEvidence.length > 0) {
                await Promise.all(submittedEvidence.map(evidence => 
                    dispatch(evidenceActions.attachToObservation(evidence.id, savedObservation.id))
                ));
            }
            
            navigate(`/observation/${savedObservation.id}`);
        } catch (err) {
            if (err.inner) {
                const observationErrors = err.inner.reduce((acc, curr) => {
                    acc[curr.path] = curr.message;
                    return acc;
                }, {});
                setErrors(observationErrors);
            } else {
                setErrors({ general: 'An unexpected error occurred' });
            }
        }
    };

    return (
        <Sheet sx={{ p: 4 }}>
            <Card>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Typography variant="h4">Add New Observation</Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <ObservationForm 
                            observation={observation} 
                            errors={errors}
                            notes={initialNotes}
                            evidence={[]}
                            onSubmit={handleSubmit}
                        />
                    </Grid>
                </Grid>
            </Card>
        </Sheet>
    );
};

export default ObservationAdd;